import { DEFAULT_BIOLOGICAL_APPEARANCE_SYSTEM_INSTRUCTION } from '@nest-react/domain';

export type AgentSkillIconTone = 'violet' | 'rose';

export type AgentSkill = {
  id: string;
  label: string;
  description: string;
  labelTranslationKey?: string;
  descriptionTranslationKey?: string;
  icon: string;
  iconTone?: AgentSkillIconTone;
  promptTemplate: string;
  systemPrompt?: string;
  imageRecognitionPrompt?: string;
  followUpChat?: boolean;
  autoActivateOnIntent?: boolean;
  interactiveFlow?: boolean;
  hiddenFromMenu?: boolean;
};

export const AD_CAMPAIGN_SKILL_ID = 'ad-campaign';
export const AD_VIDEO_SKILL_ID = 'ad-video';

const AD_CAMPAIGN_SYSTEM_PROMPT = `You are a senior integrated campaign director for a media studio. Your job is to turn briefs and reference visuals into finished cross-platform ad campaigns with the right mix of image and video assets.

When responding, use markdown and include these sections when relevant:
1. **Campaign Overview** — objective, audience, platforms, tone
2. **Core Message** — single-minded proposition and proof points
3. **Asset Plan** — which image and/or video deliverables should be produced for each platform
4. **Creative Concepts** — 2–4 distinct angles with hooks
5. **Ad Copy Variants** — headlines, primary text, and CTA for each platform requested
6. **Visual Direction** — scene, product treatment, color, motion notes, and production constraints
7. **A/B Tests** — what to test first and success metrics

Be specific, production-ready, and concise. Do not invent regulated claims unless the user provides them.

${DEFAULT_BIOLOGICAL_APPEARANCE_SYSTEM_INSTRUCTION}`;

const AD_CAMPAIGN_PROMPT_TEMPLATE = `Build an ad campaign for [product or brand name], from concept to finished video and image assets for all platforms.

Target audience: [who should this reach]
Platforms: [e.g. Instagram, TikTok, YouTube Shorts, Meta, website]
Campaign goal: [awareness / conversion / product launch]
Deliverables: [auto / image assets / video assets / both]
Key message: [optional — main benefit or offer]
Tone: [optional — e.g. premium, playful, urgent]
Constraints: [optional — claims to avoid, mandatory elements]`;

const AD_CAMPAIGN_IMAGE_PROMPT =
  'Describe this product photo for exact reproduction in ad visuals. Include product type, exact silhouette or cut, colors, fabric or material, patterns, logos, hardware, neckline, sleeve, hem, fit, and any distinguishing details. Describe only what is visible — do not suggest changes or alternatives.';

const AD_VIDEO_SYSTEM_PROMPT = `You are a senior advertising video director for a media studio. Your job is to turn briefs and product references into short-form ad videos.

When responding, use markdown and keep guidance production-ready for 4–30 second spots on Reels, TikTok, and Shorts. Focus on hook, motion, shot plan, and CTA.

${DEFAULT_BIOLOGICAL_APPEARANCE_SYSTEM_INSTRUCTION}`;

const AD_VIDEO_PROMPT_TEMPLATE = `Create an ad video for [product or brand name].

Target audience: [who should this reach]
Platforms: [e.g. TikTok, Instagram Reels, YouTube Shorts]
Campaign goal: [awareness / conversion / product launch]
Hook: [first 3 seconds]
Key message: [optional]
Tone: [optional]
Constraints: [optional]`;

const AD_VIDEO_IMAGE_PROMPT =
  'Describe this product for a short advertising video. Include visible product type, colors, materials, logos, silhouette, and distinctive details. Note anything that could move or be showcased in motion. Describe only what is visible.';

export const agentSkills: AgentSkill[] = [
  {
    id: 'timeline',
    label: 'Timeline',
    description: 'Plan milestones from concept through final delivery.',
    labelTranslationKey: 'agentUi.skill.timeline.label',
    descriptionTranslationKey: 'agentUi.skill.timeline.description',
    icon: 'timeline',
    promptTemplate:
      'Build a production timeline for [project name] with milestones for concept, asset creation, review, and delivery.',
  },
  {
    id: AD_CAMPAIGN_SKILL_ID,
    label: 'Ad Campaign',
    description:
      'Build a campaign from concept to finished video and image assets for every platform.',
    labelTranslationKey: 'agentUi.skill.adCampaign.label',
    descriptionTranslationKey: 'agentUi.skill.adCampaign.description',
    icon: 'skill',
    iconTone: 'violet',
    promptTemplate: AD_CAMPAIGN_PROMPT_TEMPLATE,
    systemPrompt: AD_CAMPAIGN_SYSTEM_PROMPT,
    imageRecognitionPrompt: AD_CAMPAIGN_IMAGE_PROMPT,
    followUpChat: true,
    autoActivateOnIntent: true,
  },
  {
    id: AD_VIDEO_SKILL_ID,
    label: 'Ad Video',
    description: 'Create a short-form ad video with a hook and call to action.',
    labelTranslationKey: 'agentUi.skill.adVideo.label',
    descriptionTranslationKey: 'agentUi.skill.adVideo.description',
    icon: 'skill',
    iconTone: 'rose',
    promptTemplate: AD_VIDEO_PROMPT_TEMPLATE,
    systemPrompt: AD_VIDEO_SYSTEM_PROMPT,
    imageRecognitionPrompt: AD_VIDEO_IMAGE_PROMPT,
    followUpChat: true,
    autoActivateOnIntent: true,
    hiddenFromMenu: true,
  },
  {
    id: 'mood-board',
    label: 'Mood Board',
    description: 'Shape your project with a focused set of visual directions.',
    labelTranslationKey: 'agentUi.skill.moodBoard.label',
    descriptionTranslationKey: 'agentUi.skill.moodBoard.description',
    icon: 'skill',
    promptTemplate:
      'Create a mood board direction for [project]. Describe palette, typography, references, and visual tone.',
  },
  {
    id: 'motion-graphic',
    label: 'Motion Graphic',
    description: 'Turn text, charts, and graphics into a motion concept.',
    labelTranslationKey: 'agentUi.skill.motionGraphic.label',
    descriptionTranslationKey: 'agentUi.skill.motionGraphic.description',
    icon: 'scroll',
    promptTemplate:
      'Outline a motion graphic concept for [topic] including pacing, transitions, and on-screen text.',
  },
];

export const composerMenuSkills = agentSkills.filter(
  skill => !skill.hiddenFromMenu
);

/** @deprecated Use composerMenuSkills. Interactive SSE flows were removed. */
export const interactiveAgentSkills = composerMenuSkills;

export const getAgentSkill = (id: string | null): AgentSkill | undefined =>
  id ? agentSkills.find(skill => skill.id === id) : undefined;

export const getAgentSkillByLabel = (label: string): AgentSkill | undefined =>
  agentSkills.find(skill => skill.label === label);
